import { Link } from "@tanstack/react-router";
import { Phone, MessageCircle, CheckCircle, AlertTriangle, MapPin, ArrowRight } from "lucide-react";
import { getBusinessConfig, areas as ALL_AREAS } from "@/lib/site-config";
import { FAQAccordion } from "@/components/FAQAccordion";
import { CTABlock } from "@/components/CTABlock";
import { AreaPageCTAs } from "@/components/AreaPageCTAs";
import { ReassuranceBanner } from "@/components/ReassuranceBanner";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { WhyChooseWoodseer } from "@/components/WhyChooseWoodseer";
import { AreaDriverLinks } from "@/components/AreaDriverLinks";

export interface PriorityPageProps {
  areaSlug: string;
  areaName: string;
  serviceSlug: string;          // e.g. "brakes", "tyres"
  serviceName: string;          // e.g. "Brakes"
  parentServiceSlug?: string;   // e.g. "mechanical-repairs" — for the AreaPageCTAs parent link
  hero: string;
  localBenefit: string;
  whatsIncluded: string[];
  commonIssues: string[];
  process: { step: string; desc: string }[];
  whyUs: string[];
  faqs: { q: string; a: string }[];
}

export function PriorityServiceAreaPage(p: PriorityPageProps) {
  const biz = getBusinessConfig();
  const area = ALL_AREAS.find((a) => a.slug === p.areaSlug);

  return (
    <>
      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <div className="inline-flex items-center gap-2 text-primary text-sm font-medium mb-3">
            <MapPin className="h-4 w-4" />
            {p.areaName}, East London
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-foreground tracking-tight">
            {p.serviceName} in {p.areaName}
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {p.hero}
          </p>
          <p className="mt-3 text-sm text-primary font-medium">✓ {p.localBenefit}</p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href={biz.phoneHref} className="inline-flex items-center gap-2 rounded-md bg-primary px-7 py-3 text-base font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg shadow-primary/25">
              <Phone className="h-5 w-5" /> Call {biz.phone}
            </a>
            <a href={biz.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-md border border-[#25D366]/30 bg-[#25D366]/10 px-6 py-3 text-base font-semibold text-[#25D366] hover:bg-[#25D366]/20 transition-colors">
              <MessageCircle className="h-5 w-5" /> WhatsApp
            </a>
            <Link to="/book" className="inline-flex items-center gap-2 rounded-md bg-foreground px-6 py-3 text-base font-semibold text-background hover:opacity-90 transition-opacity">
              Book online
            </Link>
          </div>
        </div>
      </section>

      <ReassuranceBanner />

      <AreaPageCTAs
        areaSlug={p.areaSlug}
        areaName={p.areaName}
        serviceSlug={p.parentServiceSlug}
        serviceName={p.serviceName}
      />

      <AnimatedSection>
        <section className="py-14 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center">What's included</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
              {p.whatsIncluded.map((item) => (
                <StaggerItem key={item}>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />{item}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <section className="py-14 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center">Common issues we see</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {p.commonIssues.map((issue) => (
                <StaggerItem key={issue}>
                  <div className="flex items-start gap-3 rounded-xl border border-border bg-card/50 p-4 text-sm text-muted-foreground">
                    <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />{issue}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <section className="py-14 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center">How we work</h2>
            <ol className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {p.process.map((s, i) => (
                <li key={s.step} className="rounded-xl border border-border bg-background p-4">
                  <p className="text-xs font-bold text-primary">Step {i + 1}</p>
                  <p className="mt-1 font-semibold text-foreground">{s.step}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{s.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <section className="py-14 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center">Why {p.areaName} drivers choose us</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {p.whyUs.map((r) => (
                <StaggerItem key={r}>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />{r}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <FAQAccordion faqs={p.faqs} title={`${p.serviceName} in ${p.areaName} — FAQs`} />
      </AnimatedSection>

      {area && (
        <section className="py-10 bg-card border-y border-border">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <Link
              to="/areas/$area"
              params={{ area: p.areaSlug }}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80"
            >
              All services in {p.areaName} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </section>
      )}

      <AreaDriverLinks areaSlug={p.areaSlug} areaName={p.areaName} />

      <WhyChooseWoodseer page={`${p.areaSlug}-${p.serviceSlug}`} />

      <CTABlock
        headline={`Book ${p.serviceName} in ${p.areaName}`}
        subtext={`Call, WhatsApp, or request a booking online and we'll get your ${p.serviceName.toLowerCase()} sorted.`}
        service={p.serviceName}
      />
    </>
  );
}
